/**
 * Competitive Benchmark Chart
 * Anonymous competitive intelligence comparing game vs genre vs platform averages
 * Solis Games - Phase 2.2 Implementation
 * THE SECRET SAUCE: No other platform offers this
 */

import React from 'react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip
} from 'recharts';
import Icon from '../../../components/AppIcon';

const CompetitiveBenchmarkChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading competitive benchmarks...</p>
        </div>
      </div>
    );
  }

  if (!data || data.error || !data.yourGame) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="text-center py-12">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Benchmark Data Available</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Competitive benchmarks will appear once your game has been played for at least 30 days.
            We need sufficient data to provide meaningful comparisons.
          </p>
        </div>
      </div>
    );
  }

  // Prepare radar chart data - normalize all metrics to 0-100 scale
  const radarData = [
    {
      metric: 'Engagement',
      yourGame: Math.min(100, data.yourGame.engagement_score || 0),
      genreAvg: Math.min(100, data.genreAverage.avg_engagement || 0),
      platformAvg: Math.min(100, data.platformAverage.avg_engagement || 0),
      fullMark: 100
    },
    {
      metric: 'Session Length',
      yourGame: Math.min(100, ((data.yourGame.avg_session_minutes || 0) / 60) * 100),
      genreAvg: Math.min(100, ((data.genreAverage.avg_session || 0) / 60) * 100),
      platformAvg: Math.min(100, ((data.platformAverage.avg_session || 0) / 60) * 100),
      fullMark: 100
    },
    {
      metric: 'Quality Score',
      yourGame: data.yourGame.avg_quality_score || 0,
      genreAvg: data.genreAverage.avg_engagement * 0.6 || 0, // Estimate based on engagement
      platformAvg: data.platformAverage.avg_engagement * 0.5 || 0,
      fullMark: 100
    },
    {
      metric: 'Player Count',
      yourGame: Math.min(100, ((data.yourGame.total_unique_players || 0) / 1000) * 50),
      genreAvg: Math.min(100, ((data.genreAverage.avg_players || 0) / 1000) * 50),
      platformAvg: Math.min(100, ((data.platformAverage.avg_players || 0) / 1000) * 50),
      fullMark: 100
    },
    {
      metric: 'Session Frequency',
      yourGame: Math.min(100, ((data.yourGame.total_sessions || 0) / (data.yourGame.total_unique_players || 1)) * 15),
      genreAvg: 50, // Normalized baseline
      platformAvg: 50,
      fullMark: 100
    }
  ];

  // Prepare bar chart data for easier comparison
  const barChartData = [
    {
      name: 'Your Game',
      engagement: data.yourGame.engagement_score || 0,
      sessions: data.yourGame.total_sessions || 0,
      players: data.yourGame.total_unique_players || 0
    },
    {
      name: 'Genre Avg',
      engagement: data.genreAverage.avg_engagement || 0,
      sessions: data.genreAverage.avg_sessions || 0,
      players: data.genreAverage.avg_players || 0
    },
    {
      name: 'Platform Avg',
      engagement: data.platformAverage.avg_engagement || 0,
      sessions: data.platformAverage.avg_sessions || 0,
      players: data.platformAverage.avg_players || 0
    }
  ];

  const getRankColor = (rank) => {
    if (rank >= 75) return {
      bg: 'bg-gradient-to-br from-green-500/20 to-emerald-500/20',
      border: 'border-green-500/30',
      text: 'text-green-500',
      icon: 'Trophy'
    };
    if (rank >= 50) return {
      bg: 'bg-gradient-to-br from-yellow-500/20 to-amber-500/20',
      border: 'border-yellow-500/30',
      text: 'text-yellow-500',
      icon: 'Award'
    };
    return {
      bg: 'bg-gradient-to-br from-orange-500/20 to-red-500/20',
      border: 'border-orange-500/30',
      text: 'text-orange-500',
      icon: 'Target'
    };
  };

  const rankStyle = getRankColor(data.percentileRank);

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border bg-gradient-to-r from-primary/5 to-secondary/5">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="BarChart3" size={24} className="text-primary" />
              Competitive Benchmarking
              <span className="px-2 py-1 text-xs font-semibold bg-purple-500/20 text-purple-500 rounded-full">
                EXCLUSIVE
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Anonymous comparison against {data.yourGame.primary_genre} games and platform-wide metrics
            </p>
          </div>
        </div>
      </div>

      {/* Percentile Rank Banner */}
      <div className={`p-6 border-b border-border ${rankStyle.bg} border-l-4 ${rankStyle.border}`}>
        <div className="flex items-center gap-4">
          <div className={`p-4 rounded-full bg-card border-2 ${rankStyle.border}`}>
            <Icon name={rankStyle.icon} size={32} className={rankStyle.text} />
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className={`text-4xl font-bold ${rankStyle.text}`}>
                {data.percentileRank}th
              </span>
              <span className="text-lg text-foreground">Percentile</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Your game ranks better than <span className="font-semibold text-foreground">{data.percentileRank}%</span> of{' '}
              <span className="font-semibold text-foreground">{data.yourGame.primary_genre}</span> games on the platform
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-foreground">
              {data.yourGame.engagement_score || 0}
            </div>
            <div className="text-xs text-muted-foreground">Engagement Score</div>
          </div>
        </div>
      </div>

      {/* Radar Chart */}
      <div className="p-6 border-b border-border">
        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Target" size={18} />
          Multi-Dimensional Performance
        </h4>
        <ResponsiveContainer width="100%" height={400}>
          <RadarChart data={radarData}>
            <PolarGrid stroke="hsl(var(--border))" />
            <PolarAngleAxis
              dataKey="metric"
              stroke="hsl(var(--muted-foreground))"
              style={{ fontSize: 13, fontWeight: 500 }}
            />
            <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="hsl(var(--border))" />
            <Radar
              name="Your Game"
              dataKey="yourGame"
              stroke="#3b82f6"
              fill="#3b82f6"
              fillOpacity={0.6}
              strokeWidth={2}
            />
            <Radar
              name="Genre Average"
              dataKey="genreAvg"
              stroke="#f59e0b"
              fill="#f59e0b"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Radar
              name="Platform Average"
              dataKey="platformAvg"
              stroke="#8b5cf6"
              fill="#8b5cf6"
              fillOpacity={0.2}
              strokeWidth={2}
            />
            <Legend />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Bar Chart Comparison */}
      <div className="p-6 border-b border-border bg-muted/5">
        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="BarChart2" size={18} />
          Key Metrics Comparison
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground mb-2">Engagement Score</div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-blue-500 font-medium">You</span>
                <span className="text-lg font-bold text-foreground">{data.yourGame.engagement_score || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-orange-500 font-medium">Genre</span>
                <span className="text-sm text-muted-foreground">{(data.genreAverage.avg_engagement || 0).toFixed(0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-purple-500 font-medium">Platform</span>
                <span className="text-sm text-muted-foreground">{(data.platformAverage.avg_engagement || 0).toFixed(0)}</span>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground mb-2">Session Length (min)</div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-blue-500 font-medium">You</span>
                <span className="text-lg font-bold text-foreground">{(data.yourGame.avg_session_minutes || 0).toFixed(1)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-orange-500 font-medium">Genre</span>
                <span className="text-sm text-muted-foreground">{(data.genreAverage.avg_session || 0).toFixed(1)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-purple-500 font-medium">Platform</span>
                <span className="text-sm text-muted-foreground">{(data.platformAverage.avg_session || 0).toFixed(1)}</span>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground mb-2">Unique Players</div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-blue-500 font-medium">You</span>
                <span className="text-lg font-bold text-foreground">{(data.yourGame.total_unique_players || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-orange-500 font-medium">Genre</span>
                <span className="text-sm text-muted-foreground">{Math.round(data.genreAverage.avg_players || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-purple-500 font-medium">Platform</span>
                <span className="text-sm text-muted-foreground">{Math.round(data.platformAverage.avg_players || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actionable Insights */}
      <div className="p-6">
        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Lightbulb" size={18} className="text-yellow-500" />
          AI-Powered Insights & Recommendations
        </h4>
        {data.insights && data.insights.length > 0 ? (
          <div className="space-y-3">
            {data.insights.map((insight, idx) => {
              const iconName = insight.type === 'success' ? 'CheckCircle' :
                             insight.type === 'warning' ? 'AlertCircle' :
                             insight.type === 'achievement' ? 'Trophy' : 'Info';
              const colorClass = insight.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-500' :
                                insight.type === 'warning' ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-500' :
                                insight.type === 'achievement' ? 'bg-purple-500/10 border-purple-500/20 text-purple-500' :
                                'bg-blue-500/10 border-blue-500/20 text-blue-500';

              return (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border ${colorClass}`}
                >
                  <div className="flex items-start gap-3">
                    <Icon name={iconName} size={20} className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold text-foreground text-sm mb-1">{insight.title}</div>
                      <div className="text-sm text-muted-foreground">{insight.message}</div>
                      {insight.actionable && (
                        <div className="mt-2 flex items-center gap-1 text-xs font-medium">
                          <Icon name="ArrowRight" size={14} />
                          <span>Actionable Insight</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="Sparkles" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Insights will appear once we have enough data to generate meaningful recommendations.
            </p>
          </div>
        )}
      </div>

      {/* Footer Note */}
      <div className="p-4 bg-muted/30 border-t border-border">
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Icon name="Shield" size={14} />
          <span>
            All comparisons are anonymous. Your competitors cannot see your specific metrics.
            This feature is exclusive to Solis Games.
          </span>
        </div>
      </div>
    </div>
  );
};

export default CompetitiveBenchmarkChart;
